import { ApplicantStatus } from '@components/templates/employer/applicant-list/functions';
import { McFlowCardData } from '@components/templates/employer/applicant-list/flow-mc/flow-types';
import { McKeywordsTarget, McLastActivityLevel, McProfileSource, McUnifiedProfileQueryParams } from '@components/templates/employer/applicant-list/flow-mc/types';

export type McApplicantsSourceFilter = 'applicants' | 'recommended' | 'uploaded';
export type McApplicantsSourceFilterValue = McApplicantsSourceFilter | null;
export type McApplicantsSourceFilterSet = Set<McApplicantsSourceFilter>;
export const ALL_APPLICANTS_SOURCE_FILTERS: McApplicantsSourceFilter[] = ['applicants', 'recommended', 'uploaded'];
export type McFlowColumnType = 'rejected' | 'applicants' | 'shortlist' | 'interview';

// ============================================================================
// Filter Constants
// ============================================================================

/** Last activity level options for filter dropdown */
export const MC_LAST_ACTIVITY_LEVEL_OPTIONS: { value: McLastActivityLevel; labelKey: string }[] = [
  { value: 'last-week', labelKey: 'global.last_activity_7' },
  { value: 'last-month', labelKey: 'global.last_activity_30' },
  { value: 'last-3-months', labelKey: 'global.last_activity_90' },
  { value: 'last-6-months', labelKey: 'global.last_activity_180' },
  { value: 'last-year', labelKey: 'global.last_activity_365' },
];

/** Keywords target options for search dropdown */
export const MC_KEYWORDS_TARGET_OPTIONS: { value: McKeywordsTarget; labelKey: string }[] = [
  { value: '', labelKey: 'mc.filter.keywordsTarget.all' },
  { value: 'name', labelKey: 'mc.filter.keywordsTarget.name' },
  { value: 'experienceCompanyNames', labelKey: 'mc.filter.keywordsTarget.company' },
  { value: 'experienceNames', labelKey: 'mc.filter.keywordsTarget.experience' },
];

/** Filter label keys for displaying active filter chips */
export const MC_FILTER_LABEL_KEYS: Record<string, string> = {
  new: 'mc.filter.label.new',
  keywords: 'mc.filter.label.keywords',
  keywordsTarget: 'mc.filter.label.keywordsTarget',
  withInterview: 'mc.filter.label.withInterview',
  lastActivityLevel: 'mc.filter.label.lastActivity',
  experienceLevels: 'mc.filter.label.experienceLevels',
  studyLevels: 'mc.filter.label.studyLevels',
  locations: 'mc.filter.label.locations',
  spokenLanguages: 'mc.filter.label.spokenLanguages',
};

/** Keys of filter params that should be treated as active filters */
export const MC_ACTIVE_FILTER_KEYS = [
  'new',
  'keywords',
  'withInterview',
  'lastActivityLevel',
  'experienceLevels',
  'studyLevels',
  'locations',
  'spokenLanguages',
] as const;

export type McActiveFilterKey = typeof MC_ACTIVE_FILTER_KEYS[number];

/** Study level key to translation key mapping */
export const MC_STUDY_LEVEL_LABELS: Record<string, string> = {
  '0': 'global.study.level.none',
  '1': 'global.study.level.high',
  '2': 'global.study.level.college',
  '3': 'global.study.level.universityprogress',
  '4': 'global.study.level.universitygraduate',
  '5': 'global.study.level.postuniversitary',
  '6': 'global.study.level.mba',
  '7': 'global.study.level.doctorship',
  '8': 'global.study.level.courses',
  '9': 'global.study.level.certifications',
};

/** Experience level key to translation key mapping */
export const MC_EXPERIENCE_LEVEL_LABELS: Record<string, string> = {
  '0_0': 'global.filter.experience.none',
  '_2': 'global.filter.experience.entry',
  '0_2': 'global.filter.experience.entry',
  '2_5': 'global.filter.experience.middle',
  '5_10': 'global.filter.experience.senior',
  '10_': 'global.filter.experience.executive',
};

// ============================================================================
// Column & Source Constants
// ============================================================================


export const mcApplicantsSourceFilterLabelKeys: Record<McApplicantsSourceFilter, string> = {
  applicants: 'flow.column.applicants',
  recommended: 'flow.column.recommended',
  uploaded: 'mc.applicant.uploaded',
};

export const mcFlowColumnStatusMap: Record<McFlowColumnType, ApplicantStatus> = {
  rejected: ApplicantStatus.REJECTED,
  applicants: ApplicantStatus.NEW,
  shortlist: ApplicantStatus.SHORTLIST,
  interview: ApplicantStatus.INTERVIEW,
};

export const mcFlowColumnStatusesQueryMap: Record<McFlowColumnType, string> = {
  rejected: `${ApplicantStatus.REJECTED}`,
  applicants: `${ApplicantStatus.NEW},${ApplicantStatus.VIEWED}`,
  shortlist: `${ApplicantStatus.SHORTLIST}`,
  interview: `${ApplicantStatus.INTERVIEW},${ApplicantStatus.OFFER},${ApplicantStatus.HIRED}`,
};

const normalizeStatusesQuery = (statuses?: string) => (
  statuses
    ?.split(',')
    .map((status) => status.trim())
    .filter(Boolean)
    .sort((left, right) => Number(left) - Number(right))
    .join(',')
);

export const getMcFlowStatusContext = (
  queryParams?: Pick<McUnifiedProfileQueryParams, 'status' | 'statuses'>,
) => {
  if (typeof queryParams?.status === 'number') {
    return queryParams.status;
  }

  const normalizedStatuses = normalizeStatusesQuery(queryParams?.statuses);
  if (!normalizedStatuses) return undefined;

  return (Object.entries(mcFlowColumnStatusesQueryMap).find(([, statuses]) => (
    normalizeStatusesQuery(statuses) === normalizedStatuses
  ))?.[0] as McFlowColumnType | undefined);
};

const mcApplicantsSourceFilterProfileSources: Record<McApplicantsSourceFilter, McProfileSource[]> = {
  applicants: ['applicant'],
  recommended: ['recommended_candidate'],
  uploaded: ['saved_profile', 'matching_candidate'],
};

export const filterMcApplicantsSourceItems = (
  items: McFlowCardData[],
  filter: McApplicantsSourceFilterValue,
) => {
  if (!filter) return items;

  return items.filter((item) => {
    const profileSource = item.mcProfileSource;

    if (!profileSource) return false;

    return mcApplicantsSourceFilterProfileSources[filter].includes(profileSource);
  });
};

/** Status dropdown options shown on cards for changing status */
export const CARD_STATUS_DROPDOWN_OPTIONS = [
  {
    id: 'rejected',
    label: 'flow.column.rejected',
    statusId: ApplicantStatus.REJECTED,
    className: 'text-error',
  },
  {
    id: 'interview',
    label: 'flow.column.interview',
    statusId: ApplicantStatus.INTERVIEW,
    className: 'text-secondary',
  },
  {
    id: 'shortlist',
    label: 'flow.column.shortlist',
    statusId: ApplicantStatus.SHORTLIST,
    className: 'text-primary-light',
  },
  {
    id: 'unprocessed',
    label: 'mc.status.label.unprocessed',
    statusId: ApplicantStatus.NEW,
    className: 'text-ink-medium',
  },
];
